import type { RouterOutputs } from "@apps/server/trpc/routers";
import { Box, Button, Container, Divider, Grid, Group, Table, Title } from "@mantine/core";
import { IconDeviceFloppy, IconPlus, IconRestore } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { CrmField } from "~/components/input-fields/crm-field";
import { useCrmTableView } from "~/hooks/use-crm-table-view";
import { trpc } from "~/lib/trpc";
import { DealStatusBadge } from "../deals/$dealId";

type TCustomer = RouterOutputs["customer"]["getCustomer"];

export const Route = createFileRoute("/_authenticated/customers/$customerId")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc }, params }) => {
    const customerId = Number(params.customerId);
    const [customer] = await Promise.all([
      queryClient.ensureQueryData(
        trpc.customer.getCustomer.queryOptions({
          id: customerId,
        }),
      ),
    ]);

    if (!customer) throw notFound();

    return { customer };
  },
  head: ({ loaderData }) => {
    const customer = loaderData?.customer;
    if (!customer) return {};
    const title = `Kund - ${customer.name ? customer.name : customer.id}`;

    return {
      meta: [{ title }],
    };
  },
});

function RouteComponent() {
  const { customer } = Route.useLoaderData()
  return <CustomerView customerId={customer.id} initialData={customer} />;
}

export function CustomerView(props: { customerId: number; initialData?: TCustomer }) {
  const view = useCrmTableView("crm.customer", {
    id: props.customerId,
    initialData: props.initialData,
    fields: ["name", "email", "phone", "identificationNumber", "type", "businessRepresentative"],
  });

  const navigate = useNavigate();

  return (
    <Container py="md" style={{ minHeight: "100dvh" }}>
      <Box>
        <Title order={2}>{view.values?.name ?? `Kund - ${props.customerId}`}</Title>
        <Divider my="md" />
      </Box>
      <Group justify="flex-end" mb="lg">
        <Button.Group>
          <Button
            size="xs"
            variant="light"
            disabled={!view.isDirty || view.isUpdatePending}
            leftSection={<IconRestore size={16} />}
            onClick={view.reset}>
            Reset
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDeviceFloppy size={16} />}
            loading={view.isUpdatePending}
            disabled={!view.isDirty}
            onClick={view.saveChanges}>
            Save
          </Button>
        </Button.Group>
      </Group>
      <Grid style={{ height: "100%" }} gutter="md">
        <Grid.Col span={6}>
          <CrmField {...view.getFieldProps("name")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CrmField {...view.getFieldProps("email")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CrmField {...view.getFieldProps("identificationNumber")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CrmField {...view.getFieldProps("phone")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CrmField {...view.getFieldProps("type")} />
        </Grid.Col>
        {view.values?.type === "business" && (
          <Grid.Col span={6}>
            <CrmField {...view.getFieldProps("businessRepresentative")} />
          </Grid.Col>
        )}
      </Grid>
      <Divider mt={100} mb="sm" labelPosition="left" label={<Title order={2}>Affärer</Title>} />
      <Group justify="flex-end">
        <Button
          variant="light"
          leftSection={<IconPlus size={14} />}
          size="xs"
          onClick={() => navigate({ to: "/deals/new", search: { customerId: props.customerId } })}>
          Ny affär
        </Button>
      </Group>
      <CustomerDeals customerId={props.customerId} />
    </Container>
  );
}

function CustomerDeals(props: { customerId: number }) {
  const deals = useQuery(trpc.customer.getDeals.queryOptions({ id: props.customerId }));
  const dealFieldConfigs = useQuery(trpc.crm.getFieldConfigsByTable.queryOptions({ table: "crm.deal" }));
  const navigate = useNavigate();

  const getFieldLabel = useCallback(
    (field: string) => {
      if (!dealFieldConfigs.data) return field;

      const config = dealFieldConfigs.data[field]?.config;
      if (!config) return field;
      return config.labelSv || config.labelEn || field;
    },
    [dealFieldConfigs.data],
  );

  if (deals.isError || dealFieldConfigs.isError) return null;

  return (
    <Box>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Id</Table.Th>
            <Table.Th>{getFieldLabel("serviceCategory")}</Table.Th>
            <Table.Th>{getFieldLabel("service")}</Table.Th>
            <Table.Th>{getFieldLabel("serviceDate")}</Table.Th>
            <Table.Th>{getFieldLabel("status")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {deals.isPending || dealFieldConfigs.isPending
            ? null
            : deals.data.map((deal) => {
              return (
                <Table.Tr
                  key={deal.id}
                  onDoubleClick={() => navigate({ to: "/deals/$dealId", params: { dealId: deal.id.toString() } })}
                  style={{ cursor: "pointer" }}>
                  <Table.Td>{deal.id}</Table.Td>
                  <Table.Td>{deal.service}</Table.Td>
                  <Table.Td>{deal.serviceDate}</Table.Td>
                  <Table.Td>
                    <DealStatusBadge status={deal.status} size="md" />
                  </Table.Td>
                </Table.Tr>
              );
            })}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
